import React from 'react';
import { Box, ButtonBase } from '@mui/material';

export interface SegmentOption<T extends string | number> {
  value: T;
  label: React.ReactNode;
  icon?: React.ReactNode;
}

interface SegmentedControlProps<T extends string | number> {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: 'small' | 'medium';
  ariaLabel?: string;
}

// Pill-style tab switcher (muted track, raised active segment) used for tabs and filters.
export function SegmentedControl<T extends string | number>({ options, value, onChange, size = 'medium', ariaLabel }: SegmentedControlProps<T>) {
  const height = size === 'small' ? 28 : 30;
  return (
    <Box role="tablist" aria-label={ariaLabel} sx={{ display: 'inline-flex', alignSelf: 'flex-start', backgroundColor: 'var(--muted-bg)', borderRadius: '9px', p: '3px', gap: '2px', maxWidth: '100%', overflowX: 'auto' }}>
      {options.map(opt => {
        const active = opt.value === value;
        return (
          <ButtonBase
            key={String(opt.value)}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            sx={{
              height,
              px: 1.5,
              borderRadius: '7px',
              fontSize: size === 'small' ? 12 : 13,
              fontWeight: 500,
              gap: 0.75,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              backgroundColor: active ? 'var(--card)' : 'transparent',
              color: active ? 'var(--fg)' : 'var(--muted)',
              boxShadow: active ? '0 1px 2px rgba(0,0,0,.08)' : 'none',
              '& svg': { fontSize: 15 },
            }}
          >
            {opt.icon}
            {opt.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}
